import React, { useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { MessageCircle, Check, Star, ShieldCheck, Clock3, ArrowLeft } from "lucide-react";
import "./StartPage.css";

const WA_NUMBER = "917996325666";

export default function StartPage() {
  const [params] = useSearchParams();
  const name = (params.get("name") || "").trim();
  const role = (params.get("role") || "").trim();
  const src = params.get("src") || "start_page";

  const firstName = name.split(" ")[0] || "there";

  const waHref = useMemo(() => {
    const msg = role
      ? `Hey Shubham, I'm ${name || "there"}. I want to start with BulkJobApply for ${role}. Please share the next steps.`
      : `Hey Shubham, I'm ${name || "there"}. I want to start with BulkJobApply. Please share the next steps.`;
    return `https://api.whatsapp.com/send?phone=${WA_NUMBER}&text=${encodeURIComponent(msg)}`;
  }, [name, role]);

  // Fire GA / Google Ads landing event + Google Ads conversion snippet on mount
  useEffect(() => {
    document.title = "You're in. Open WhatsApp | BulkJobApply";
    if (typeof window.gtag !== "function") return;

    // Guard against React StrictMode double-invocation (dev only)
    // AND SPA re-navigation from within the same session.
    try {
      if (!window.__bja_start_view_fired) {
        window.gtag("event", "start_page_view", {
          event_category: "conversion",
          event_label: src,
        });
        window.__bja_start_view_fired = true;
      }
    } catch (_) {}

    // Google Ads. "Digi - BJA - USA - Lead Collected" conversion event.
    // Fires once per session, immediately after any inline lead form submit.
    try {
      if (!window.__bja_lead_conv_fired) {
        window.gtag("event", "conversion", {
          send_to: "AW-18360213648/anGtCPzc5OwcEJDB6rJE",
        });
        window.__bja_lead_conv_fired = true;
      }
    } catch (_) {}

    // Meta Pixel, fire PageView (SPA) + Lead event once per session.
    try {
      if (typeof window.fbq === "function") {
        window.fbq("track", "PageView");
        if (!window.__bja_fb_lead_fired) {
          window.fbq("track", "Lead");
          window.__bja_fb_lead_fired = true;
        }
      }
    } catch (_) {}

    // ChatGPT Ads pixel: registration_completed conversion, once per session.
    try {
      if (typeof window.oaiq === "function" && !window.__bja_oai_conv_fired) {
        window.oaiq("measure", "registration_completed", { type: "customer_action" });
        window.__bja_oai_conv_fired = true;
      }
    } catch (_) {}
  }, [src]);

  const handleWaClick = () => {
    if (typeof window.gtag_report_conversion === "function") {
      try { window.gtag_report_conversion(); } catch (_) {}
    }
    if (typeof window.gtag === "function") {
      try {
        window.gtag("event", "whatsapp_click", {
          event_category: "conversion",
          event_label: "start_page",
        });
      } catch (_) {}
    }
    // Meta Pixel, track high-intent WhatsApp click
    if (typeof window.fbq === "function") {
      try { window.fbq("track", "Contact"); } catch (_) {}
    }
  };

  return (
    <div className="start-page">
      <div className="start-shell">
        {/* Header */}
        <header className="start-header">
          <Link to="/" className="start-back" data-testid="start-back">
            <ArrowLeft className="w-4 h-4" />
            <span>BulkJobApply</span>
          </Link>
        </header>

        <div className="start-grid">
          {/* Main column */}
          <main className="start-main">
            <div className="start-badge" data-testid="start-badge">
              <Check className="w-4 h-4" />
              <span>Almost done. One final step.</span>
            </div>

            <h1 className="start-headline" data-testid="start-headline">
              Hey {firstName} 👋 <br />
              Open WhatsApp to start your{" "}
              <span className="start-hl">
                {role || "job search"}
              </span>{" "}
              plan.
            </h1>

            <p className="start-sub" data-testid="start-sub">
              We&apos;ve prefilled a message for you. Just tap the button. Our team
              replies within <strong>15 minutes</strong> with your personal plan.
            </p>

            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="start-wa-btn"
              data-testid="start-wa-btn"
              onClick={handleWaClick}
            >
              <MessageCircle className="w-6 h-6" />
              <span>Open WhatsApp now</span>
            </a>

            <div className="start-fine" data-testid="start-fine">
              Doesn&apos;t open? <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWaClick}
                data-testid="start-wa-fallback"
              >Click here instead</a>. Or save this number: <strong>+91 79963 25666</strong>.
            </div>

            {/* What happens next. 3-step */}
            <div className="start-steps" data-testid="start-steps">
              <h2 className="start-steps-title">What happens in the next 15 minutes</h2>
              <ol className="start-steps-list">
                <li>
                  <span className="start-step-num">1</span>
                  <div>
                    <strong>Our team messages you on WhatsApp.</strong>
                    <p>Real human, not a bot. They review your role + resume before replying.</p>
                  </div>
                </li>
                <li>
                  <span className="start-step-num">2</span>
                  <div>
                    <strong>Quick 20-min plan walkthrough (free).</strong>
                    <p>We show you exactly which companies and titles will call you back.</p>
                  </div>
                </li>
                <li>
                  <span className="start-step-num">3</span>
                  <div>
                    <strong>Interview calls start within 14 days on average.</strong>
                    <p>Your team hand-applies to 780+ roles a month and triple-touches every hiring manager, application, LinkedIn DM, and cold email. 10+ interviews in 90 days, guaranteed.</p>
                  </div>
                </li>
              </ol>
            </div>
          </main>

          {/* Sidebar */}
          <aside className="start-side">
            <div className="start-side-card">
              <div className="start-stars" aria-label="4.9 star rating">
                {[0,1,2,3,4].map((i) => (
                  <Star key={i} className="w-4 h-4" fill="currentColor" />
                ))}
                <span className="start-stars-label"><strong>4.9/5</strong></span>
              </div>
              <blockquote className="start-quote">
                &ldquo;3 interviews in week 1, including two FAANG. I signed an offer
                6 weeks in. Best money I&apos;ve spent on my career.&rdquo;
              </blockquote>
              <div className="start-quote-by">
                <div className="start-quote-avatar">P</div>
                <div>
                  <div className="start-quote-name">Priya S.</div>
                  <div className="start-quote-role">Senior Data Engineer · Hired at Airbnb</div>
                </div>
              </div>
            </div>

            <div className="start-badges">
              <div className="start-badge-row">
                <ShieldCheck className="w-4 h-4" />
                <span><strong>10+ interviews in 90 days</strong> guarantee</span>
              </div>
              <div className="start-badge-row">
                <Clock3 className="w-4 h-4" />
                <span>WhatsApp reply <strong>&lt; 15 min</strong> (9am to 11pm IST)</span>
              </div>
              <div className="start-badge-row">
                <Check className="w-4 h-4" />
                <span>800+ hired · 93% success rate</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
